<template>
	<div class="booker-location">
		<GlobalModalHeader @closeModal="closeMenu">{{
			type === EBookerLocationType.START
				? $t('components.booker.location_container.select_pickup_location')
				: $t('components.booker.location_container.select_dropoff_location')
		}}</GlobalModalHeader>
		<div v-if="tab !== tabs[2]" ref="searchRef" class="booker-location__search">
			<div class="booker-location__delivery">
				<GlobalBookerCarDeliveryButton :type="type" />
			</div>
			<v-text-field
				v-if="tab === tabs[0]"
				v-model="searchField"
				flatF
				density="comfortable"
				variant="solo"
				:placeholder="$t('components.booker.location_container.search_placeholder')"
				hide-details="auto"
				clearable
				persistent-clear
				class="booker-location__search-input">
				<template #prepend-inner> <GlobalIcon class="booker-location__search-icon" name="search" :height="20" /></template>
				<template #append-inner>
					<div class="booker-location__search-gps" @click="getLocation('default')">
						<GlobalIcon class="booker-location__search-gps-icon" name="gps" :height="20" />
					</div>
					<!-- <div class="booker-location__search-block">
						<GlobalIcon name="search-normal" :height="20" /></div
				> -->
				</template>
			</v-text-field>
			<v-text-field
				v-if="tab === tabs[1]"
				id="inputGoogle"
				ref="inputRef"
				v-model="searchFieldDelivery"
				flatF
				density="comfortable"
				:messages="
					!isSomeLocationsSelectable(results) && searchFieldDelivery && results?.length && results.length > 0
						? $t('components.booker.location_container.add_street_and_number')
						: ''
				"
				variant="solo"
				:placeholder="$t('components.booker.location_container.search_placeholder')"
				clearable
				persistent-clear
				class="booker-car-delivery__search-input">
				<template #prepend-inner> <GlobalIcon class="booker-car-delivery__search-icon" name="search" :height="20" /></template>
				<template #append-inner>
					<div class="booker-location__search-gps" @click="getLocation('delivery')">
						<GlobalIcon class="booker-location__search-gps-icon" name="gps" :height="20" />
					</div>
					<!-- <div class="booker-car-delivery__search-block">
						<GlobalIcon name="search-normal" :height="20" /></div
				> -->
				</template>
			</v-text-field>

			<div v-if="isMobile && type === EBookerLocationType.START" class="booker-location__other-place">
				<GlobalCheckbox v-model="bookerState.otherEndLocation">{{
					$t('components.booker.location_container.other_place_of_return')
				}}</GlobalCheckbox>
			</div>
		</div>

		<v-window v-model="tab">
			<v-window-item :value="tabs[0]">
				<Transition name="fade" mode="out-in">
					<template v-if="searching && cities?.length && items?.length">
						<div class="booker-location__content" :style="{ '--offset': locationOffset }">
							<Transition name="fade" mode="out-in">
								<div v-if="keywords.length > 0" class="booker-location__localization">
									<div class="booker-location__title">{{ $t('components.booker.location_container.popular_locations') }}</div>
									<div class="booker-location__chips">
										<v-chip
											v-for="(city, i) in keywords"
											:key="i"
											:class="{ 'v-chip--selected': selectedChip == city.name }"
											@click="selectChip(city)">
											{{ i18nService.$tDynamic('data.cities', city.name) }}
										</v-chip>
										<div class="booker-location__chip-more">+55 {{ $t('common.cities') }}</div>
									</div>
								</div>
							</Transition>
							<Transition name="fade" mode="out-in">
								<div v-if="searchingHistory && searchingHistory.length > 0" class="booker-location__localization">
									<div class="booker-location__title">{{ $t('components.booker.location_container.history') }}</div>
									<v-list class="booker-location__locations">
										<GlobalBookerLocation
											v-for="(item, i) in reversedSearchingHistory"
											:key="i"
											:item="item"
											@click="selectLocation(item)" />
									</v-list>
								</div>
							</Transition>

							<div v-if="searching.length > 0" class="booker-location__localization">
								<div class="booker-location__title">
									{{
										type === EBookerLocationType.START
											? $t('components.booker.location_container.select_address_pickup')
											: $t('components.booker.location_container.select_address_dropoff')
									}}
								</div>
								<v-list class="booker-location__locations">
									<GlobalBookerLocation v-for="(item, i) in searching" :key="i" :item="item" @click="selectLocation(item)" />
								</v-list>
							</div>
							<div v-else-if="searching.length === 0" class="booker-location__localization">
								<div class="booker-location__info">
									{{ $t('components.booker.location_container.no_results') }} „{{ searchField }}”.
								</div>
								<v-divider class="booker-location__divider"></v-divider>
								<div class="booker-location__title">{{ $t('components.booker.location_container.improve_search_results') }}</div>
								<GlobalListPoints :data="noDataPoints" />
							</div>
						</div>
					</template>
					<div v-else class="booker-location__content">
						<div class="booker-location__loader">
							<v-progress-circular indeterminate color="var(--primary-color)"></v-progress-circular>
						</div>
					</div>
				</Transition>
			</v-window-item>
			<v-window-item :value="tabs[1]">
				<GlobalBookerCarDelivery :type="type" />
			</v-window-item>
			<v-window-item :value="tabs[2]">
				<GlobalBookerDeliveryCarSummary v-model="isLocationMenuOpen" :type="type" />
			</v-window-item>
		</v-window>
		<GlobalBookerCarDeliveryModal v-model="isDeliveryCarModalOpen" :type="type" />
	</div>
</template>

<script lang="ts" setup>
import { EBookerLocationType } from '@/data/bookerData'

const tab = ref('default')
provide('tab', tab)
const tabs = ref(['default', 'delivery', 'summary'])
const searchField = ref<string>()
const bookingStore = useBookingStore()
const megaMenuStore = useMegaMenuStore()
const searchFieldDelivery = ref<string>()
const inputRef = ref()
const reservationStore = useReservationStore()
const isLocationMenuOpen = defineModel<boolean>()
const selectedChip = ref('')
const headerRef = ref<HTMLElement | null>(null)
const bookerState = inject<Ref<IBookerState>>('bookerState')!
const isDeliveryCarModalOpen = ref(false)
provide('isDeliveryCarModalOpen', isDeliveryCarModalOpen)
provide('searchFieldDelivery', searchFieldDelivery)
provide('inputRef', inputRef)
const i18nService = usei18nService()

provide('headerRef', headerRef)
const searchRef = ref<HTMLElement | null>(null)
const router = useRouter()
const props = defineProps<{
	type: EBookerLocationType
}>()

const { isMobile } = useWindowWidth()
const localePath = useLocalePath()

const { getUserLocation, getUserPreciseLocation } = useLocationService()
const locationsStore = useLocationsStore()

const results = ref<any[]>([])

provide('results', results)

const getLocation = async (type: string) => {
	if (type === 'delivery') searchFieldDelivery.value = (await getUserPreciseLocation()) ?? ''
	else searchField.value = (await getUserLocation()) ?? ''
}

watch(
	() => bookerState.value.selectedLocations[props.type]?.placeID,
	(newValue) => {
		if (!bookerState.value.selectedLocations[props.type]?.isDelivery) {
			tab.value = 'default'
			return
		}
		if (newValue && tab.value === 'default') {
			if (!isMobile.value) tab.value = 'summary'
			else {
				tab.value = 'delivery'
				isDeliveryCarModalOpen.value = true
			}
		}
	},
	{ immediate: true },
)

const selectChip = (city: any) => {
	searchField.value = city.name
}

const locationOffset = computed(() => {
	const heights = (headerRef.value?.clientHeight ?? 0) + (searchRef.value?.clientHeight ?? 0)
	return `${heights}px`
})

const noDataPoints = [{ text: 'check_your_spelling_again' }, { text: 'use_fewer_words' }, { text: 'cant_find_the_city', intercom: true }]

const cities = ref<ILocationCityModel[]>([])

const getLocationCities = async () => {
	try {
		const fetchedCities = await locationsStore.fetchLocationCities()
		cities.value = fetchedCities.filter((city) => city.isVisible).sort((a, b) => a.order - b.order)
	} catch (error) {
		console.log(error)
	}
}

const history = ref()

const items = ref<ILocationModel[] | undefined>(undefined)

const convertHistory = (locationID: number) => {
	return items.value?.find((item) => item.id === locationID)
}

const filterHistory = () => {
	if (reservationStore.getLocationHistory && items.value && items.value.length > 0) {
		const lastIDs = reservationStore.getLocationHistory.slice(-3)
		history.value = lastIDs.map((id: number) => convertHistory(id))
	}
}

// const getLocations = async () => {
// 	if (locationsStore.getFetchingLocations) return
// 	locationsStore.setFetchingLocations(true)
// 	try {
// 		const locations = reservationStore.getAllLocations
// 		if (locations && locations.length > 0) {
// 			items.value = locations
// 			return
// 		}
// 		const { data } = await ApiService.fetchAllLocations(1, 100, undefined, true)
// 		items.value = data
// 		// for (const item of items.value) {
// 		// 	// check if item has crm_id if not delete
// 		// 	if (!item?.crmID) {
// 		// 		const index = items.value?.indexOf(item)
// 		// 		items.value?.splice(index, 1)
// 		// 	}
// 		// }

// 		filterHistory()
// 		reservationStore.updateAllLocations(data)
// 	} catch (error) {
// 		console.log(error)
// 	}
// 	locationsStore.setFetchingLocations(false)
// }

const fetchItems = async () => {
	items.value = await locationsStore.fetchLocations()
}

onMounted(async () => {
	reservationStore.setLocationHistoryData()
	// await getLocations()

	await Promise.all([fetchItems(), getLocationCities()])
	filterHistory()
})

watch(
	() => [items.value, cities.value],
	() => {
		if (items.value && cities.value) {
			filterHistory()
			reservationStore.setLocationHistoryData()
		}
	},
	{ deep: true, immediate: true },
)

watch(
	() => reservationStore.getAllLocations,
	(newValue) => {
		if (newValue) {
			items.value = newValue
			// for (const item of items.value) {
			// 	// check if item has crm_id if not delete
			// 	if (!item?.crmID) {
			// 		const index = items.value?.indexOf(item)
			// 		items.value?.splice(index, 1)
			// 	}
			// }

			filterHistory()
		}
	},
	{
		immediate: true,
		deep: true,
	},
)

const keywords = computed(() => {
	if (!searchField.value) return cities.value
	// if (selectedChip.value) return cities.value
	return []
})

const searching = computed(() => {
	const newItems = items.value?.sort((a, b) => a.order - b.order)
	if (!searchField.value && !selectedChip.value) return newItems
	if (!searchField.value) return items.value
	const search = searchField.value?.toLowerCase().trim().replace(/,/g, '').split(' ')
	return (
		newItems?.filter((item) => {
			const city = item.city?.toLowerCase()
			const name = item.name?.toLowerCase()
			const nameEn = item.nameEn?.toLowerCase()
			const addressStreet = item.address_street?.toLowerCase()
			if (selectedChip.value) return city.includes(selectedChip.value?.toLowerCase())
			if (search[2])
				return (
					(city.includes(search[0]) || nameEn?.includes(search[0])) &&
					(name.includes(search[2]) || nameEn?.includes(search[2]) || addressStreet?.includes(search[2]))
				)
			else if (search[1])
				return (
					(city.includes(search[0]) || nameEn?.includes(search[0])) &&
					(name.includes(search[1]) || nameEn?.includes(search[1]) || addressStreet?.includes(search[1]))
				)
			else return city.includes(search[0]) || name.includes(search[0]) || nameEn?.includes(search[0]) || addressStreet?.includes(search[0])
		}) || []
	)
})

const searchingHistory = computed<ILocationModel[]>(() => {
	if (!searchField.value && !selectedChip.value) return history.value
	if (!searchField.value) return items.value
	const search = searchField.value?.toLowerCase()
	const locations = reservationStore.getLocationHistory.map((item) => convertHistory(item))
	return locations.filter((location) => {
		if (!location) return false
		const city = location.city?.toLowerCase()
		const name = location.name?.toLowerCase()
		if (selectedChip.value) return city?.includes(selectedChip.value?.toLowerCase())
		return city?.includes(search) || name?.includes(search)
	})
})

const selectLocation = (item: ILocationModel) => {
	bookerState.value.selectedLocations[props.type] = item
	useReservationStore().addLocationToHistory(item.id)
	if (props.type === EBookerLocationType.START && isMobile.value) {
		isLocationMenuOpen.value = false

		if (bookerState.value.otherEndLocation) {
			bookerState.value.isEndLocationMenuOpen = true
		} else {
			bookerState.value.isStartDateMenuOpen = true
			router.push(localePath({ hash: '#date' }))
		}
	} else if (props.type === EBookerLocationType.END && isMobile.value) {
		if (bookerState.value.selectedLocations.start?.isDelivery) {
			isDeliveryCarModalOpen.value = true
		} else {
			isLocationMenuOpen.value = false
			bookerState.value.isStartDateMenuOpen = true
			router.push(localePath({ hash: '#date' }))
		}
	} else isLocationMenuOpen.value = false
	bookingStore.setBookerLocations(bookerState.value.selectedLocations)
	bookingStore.updateBooking()
	megaMenuStore.clearCars()
}

const closeMenu = () => {
	isLocationMenuOpen.value = false
	if (router.currentRoute.value.hash === '#date' || router.currentRoute.value.hash === '#location') router.go(-1)
}

const closeMenuBrowser = () => {
	isLocationMenuOpen.value = false
}

if (process.client) {
	window.addEventListener('popstate', closeMenuBrowser)
}

watch(
	() => reservationStore.getLocationHistory,
	() => {
		if (reservationStore.getLocationHistory) {
			filterHistory()
		}
	},
	{ immediate: true, deep: true },
)

const searchingHistoryFiltered = computed(() => {
	return searchingHistory.value.filter((obj, index, self) => index === self.findIndex((t) => t?.id === obj?.id))
})

const reversedSearchingHistory = computed(() => searchingHistoryFiltered.value.slice(-3).reverse())

const isLocationSelectable = (item: any) => {
	const types = item.types

	if (types.includes('street_address') || types.includes('premise') || types.includes('subpremise')) {
		return true
	}
	return false
}
const isSomeLocationsSelectable = (items: any[]) => {
	return items.some((item) => isLocationSelectable(item))
}

watch(
	() => searchField.value,
	() => {
		filterHistory()
	},
)

onUnmounted(() => {
	window.removeEventListener('popstate', closeMenuBrowser)
	console.log('unmounted')
})
</script>

<style lang="scss">
@import './LocationContainer';
</style>
